import { useNotifications, useUnreadNotificationCount } from '@/api';
import {
  Badge,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
} from '@/components/atoms';
import {
  useMarkNotificationAsRead,
  useMarkAllNotificationsAsRead,
} from '@/api';
import type { Notification } from '@/types';
import { BellIcon, CheckCheckIcon } from 'lucide-react';
import { Link } from 'react-router';
import { routes } from '@/lib/constants';
import { cn } from '@/lib/utils';

function NotificationItem({
  notification,
  onMarkRead,
}: {
  notification: Notification;
  onMarkRead: (id: number) => void;
}) {
  const href =
    notification.type === 'new_order_received' && notification.metadata?.orderId
      ? routes.sales
      : undefined;

  const handleClick = () => {
    if (!notification.isRead) onMarkRead(notification.id);
  };

  return (
    <div
      className={cn(
        'group relative flex flex-col gap-2 rounded-md p-3 text-left transition-colors',
        notification.isRead
          ? 'bg-transparent text-muted-foreground'
          : 'bg-primary/5 text-foreground'
      )}
    >
      <div className="flex flex-col gap-0.5">
        {href ? (
          <Link
            to={href}
            onClick={handleClick}
            className="font-medium hover:underline focus-visible:outline-none"
          >
            {notification.title}
          </Link>
        ) : (
          <button
            type="button"
            onClick={handleClick}
            className="w-fit font-medium hover:underline focus-visible:outline-none"
          >
            {notification.title}
          </button>
        )}
        <p className="line-clamp-2 text-sm text-muted-foreground">
          {notification.message}
        </p>
        <p className="text-xs text-muted-foreground">
          {new Date(notification.createdAt).toLocaleString(undefined, {
            dateStyle: 'medium',
            timeStyle: 'short',
          })}
        </p>
      </div>

      {!notification.isRead && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-fit gap-1 text-[10px] font-medium tracking-wider text-primary/60 hover:bg-primary/10 hover:text-primary"
          onClick={(e) => {
            e.stopPropagation();
            onMarkRead(notification.id);
          }}
        >
          <CheckCheckIcon className="size-3" />
          Mark as read
        </Button>
      )}
    </div>
  );
}

function NotificationPopover() {
  const { data: notificationsData, isLoading } = useNotifications(1);
  const { data: unreadCount = 0 } = useUnreadNotificationCount();
  const markAsRead = useMarkNotificationAsRead();
  const markAllAsRead = useMarkAllNotificationsAsRead();

  const notifications = notificationsData?.data ?? [];
  const hasUnread = unreadCount > 0;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative size-9 text-primary/80 hover:text-primary"
          aria-label="Notifications"
        >
          <BellIcon className="size-5" />
          {hasUnread && (
            <Badge
              variant="primary"
              className="absolute -right-0.5 -top-0.5 aspect-square size-4 rounded-full p-0 text-[10px] font-medium flex items-center justify-center"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        className="flex w-full max-w-xs flex-col p-0"
      >
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="font-semibold">Notifications</h3>
          {hasUnread && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1.5 text-xs"
              onClick={() => markAllAsRead.mutate()}
              disabled={markAllAsRead.isPending}
            >
              <CheckCheckIcon className="size-3.5" />
              Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-[min(400px,70vh)] overflow-y-auto">
          {isLoading ? (
            <div className="space-y-2 p-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-20 w-full rounded-md" />
              ))}
            </div>
          ) : notifications.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No notifications yet
            </p>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkRead={(id) => markAsRead.mutate(id)}
                />
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export { NotificationPopover };
